import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { TrendingUp, ShoppingCart, DollarSign, ArrowUpRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid } from 'recharts'
import { z } from 'zod'

import type { DashboardFeatures } from '@/api/admin-dashboard-features-types'
import {
	getGetDashboardFeaturesOptions,
	useGetAnalytics,
	useGetDashboardFeatures,
	useListSalesChannels
} from '@/api/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/base/card'
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	CHART_PALETTE_FILLS,
	CHART_PALETTE_TOP_PRODUCTS
} from '@/components/base/chart'
import { Container } from '@/components/base/container'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Select } from '@/components/base/select'
import { Skeleton } from '@/components/base/skeleton'
import { Text } from '@/components/base/text'
import { SalesChannelMultiSelect } from '@/components/sales-channel-multi-select'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useI18n } from '@/i18n/i18n-react'
import { ChevronLeft } from '@/icons/chevron-left'
import { ChevronRight } from '@/icons/chevron-right'

import {
	AnalyticsPeriodControls,
	analyticsApiQuery,
	analyticsPeriodSearchSchema,
	KPICard,
	pickPeriodSearch,
	RevenueOverTimeChart,
	useSalesComparisonLabel
} from './analytics-shared'
import { BasketAnalyticsView, type BasketPairSortField } from './basket-analytics-view'
import { UserAnalyticsView } from './user-analytics-view'

/** Channels shown per page on the Sales by channel chart; full list stays in API response and is paged client-side. */
const SALES_BY_CHANNEL_CHART_PAGE_SIZE = 8

/** Map a Sales-screen `comparePeriod` to the report route's (absent = off, 'previous' = previous period). */
function toReportCompare(comparePeriod: string | undefined): string | undefined {
	if (comparePeriod === undefined) return 'previous'
	if (comparePeriod === 'none') return undefined
	return comparePeriod
}

const searchSchema = analyticsPeriodSearchSchema.extend({
	view: z.enum(['sales', 'users', 'basket']).optional().default('sales'),
	// Basket-view filters. Kept optional (no defaults) so they don't pollute the Sales/Users URLs;
	// the basket view applies its own minSupport default.
	productUuids: z.array(z.string()).optional(),
	categoryUuids: z.array(z.string()).optional(),
	// Products/categories to exclude from the pair tables (any pair touching one is dropped), so
	// dominant-but-irrelevant items (e.g. carrier bags) can be removed from the correlations.
	excludeProductUuids: z.array(z.string()).optional(),
	excludeCategoryUuids: z.array(z.string()).optional(),
	salesChannelUuids: z.array(z.string()).optional(),
	minSupport: z.coerce.number().int().min(1).optional(),
	// Independent server-side sort for each basket table. Omitted = per-table default order.
	productSortBy: z.enum(['coOccurrenceCount', 'support', 'confidence', 'lift']).optional(),
	productSortOrder: z.enum(['asc', 'desc']).optional(),
	categorySortBy: z.enum(['coOccurrenceCount', 'support', 'confidence', 'lift']).optional(),
	categorySortOrder: z.enum(['asc', 'desc']).optional()
})

/** Default co-occurrence threshold applied by the Basket view when the URL omits `minSupport`. */
const DEFAULT_MIN_SUPPORT = 3

/** Selectable co-occurrence thresholds for the Basket view's inline Min support control. */
const MIN_SUPPORT_CHOICES = [1, 3, 5, 10, 20]

/** A view backed by a Dashboard feature toggle is off when the toggle is hidden or disabled. */
const isToggleOff = (toggle?: { enabled?: boolean; hidden?: boolean }): boolean =>
	toggle?.hidden === true || toggle?.enabled === false

export const Route = createFileRoute('/_authenticated/_general/analytics/')({
	component: AnalyticsPage,
	validateSearch: searchSchema,
	// Only the view (and interval, used by the redirect target) affect the loader's feature gate.
	loaderDeps: ({ search: { interval, view } }) => ({ interval, view }),
	// The loader is intentionally synchronous: it only reads the already-cached dashboard features
	// to redirect away from a disabled view. It never awaits a network request, so navigation is
	// instant and never trips the router's full-page pending spinner. Each view then fetches its
	// own data via hooks and renders per-element skeletons while that data loads. On a cold cache
	// (features not yet loaded) the gate is skipped here and re-applied by the in-component effect.
	loader: ({ context, deps: { view, interval } }) => {
		if (view !== 'users' && view !== 'basket') return
		const features = context.queryClient.getQueryData<DashboardFeatures>(
			getGetDashboardFeaturesOptions(context.api, {}).queryKey
		)
		if (!features) return
		const toggle = view === 'users' ? features.userAnalytics : features.basketAnalytics
		if (isToggleOff(toggle)) {
			throw redirect({ to: '/analytics', search: { interval, view: 'sales' } })
		}
	}
})

function AnalyticsPage() {
	const { L, locale } = useI18n()
	const search = Route.useSearch()
	const {
		interval,
		view,
		periodStart,
		comparePeriod,
		compareChannels,
		productUuids,
		categoryUuids,
		excludeProductUuids,
		excludeCategoryUuids,
		salesChannelUuids,
		minSupport,
		productSortBy,
		productSortOrder,
		categorySortBy,
		categorySortOrder
	} = search
	const navigate = Route.useNavigate()
	const isMobile = useIsMobile()

	// Sales by channel is paged client-side; reset to the first (highest-grossing) page when the data set changes.
	const [channelPage, setChannelPage] = useState(0)
	useEffect(() => setChannelPage(0), [interval, periodStart])

	// Top products can be ranked by revenue or by units sold. Both lists arrive in one response, so
	// this is chart-local state (like `channelPage`) rather than a URL param - switching never refetches.
	const [topProductsMetric, setTopProductsMetric] = useState<'revenue' | 'quantity'>('revenue')

	const { data: analytics, isLoading } = useGetAnalytics(analyticsApiQuery(search), { enabled: view === 'sales' })
	const { data: dashboardFeatures } = useGetDashboardFeatures()
	const { data: salesChannelList, isLoading: salesChannelsLoading } = useListSalesChannels({
		enabled: view === 'basket'
	})

	// Fallback for the synchronous loader gate: if the current view's toggle turns out to be off
	// once features finish loading (cold cache), redirect back to Sales. The sidebar owns view
	// switching now, so the page itself only needs this guard for direct URLs to a disabled view.
	useEffect(() => {
		if (!dashboardFeatures) return
		const currentViewOff =
			(view === 'users' && isToggleOff(dashboardFeatures.userAnalytics)) ||
			(view === 'basket' && isToggleOff(dashboardFeatures.basketAnalytics))
		if (currentViewOff) {
			void navigate({ search: { interval, view: 'sales' }, replace: true })
		}
	}, [dashboardFeatures, view, interval, navigate])

	const displayCurrency = analytics?.overview.currencyCode ?? dashboardFeatures?.reportingCurrencyCode ?? 'EUR'

	// Lower bound for the period pickers: the first completed sale (UTC start-of-day), so merchants
	// can't page back into empty pre-history. Undefined when there are no sales yet (no bound).
	const earliestSelectable =
		dashboardFeatures?.earliestAnalyticsDay !== undefined
			? new Date(dashboardFeatures.earliestAnalyticsDay)
			: undefined

	const formatCurrency = (amount: { value: number; precision: number }) => {
		return L.Amount.Default({
			amount,
			currencyCode: displayCurrency
		})
	}

	const overview = analytics?.overview
	const topProducts = analytics?.topProducts
	const salesByChannel = analytics?.salesByChannel
	const hourlyDistribution = analytics?.hourlyDistribution

	const formatCurrencyAxis = (value: number) => {
		return L.Amount.Default({
			amount: { value: value, precision: 0 },
			currencyCode: displayCurrency
		})
	}

	// `hasComparison` is false when the merchant turned comparison off.
	const comparisonLabel = useSalesComparisonLabel(compareChannels, overview?.comparisonRange)
	const hasComparison = overview?.hasComparison ?? false
	const channelFilterActive = analytics?.channelFilterActive ?? false

	const topProductsByQuantity = analytics?.topProductsByQuantity
	const topProductsSource = topProductsMetric === 'revenue' ? topProducts : topProductsByQuantity
	const topProductsMetricLabel =
		topProductsMetric === 'revenue' ? L.Analytics.ChartLabels.Revenue() : L.Analytics.ChartLabels.Quantity()

	// Quantity can be fractional for weighed items, so cap at two decimals rather than assuming integers.
	const formatCount = (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 })

	const topProductsChartData =
		topProductsSource?.map(p => ({
			name: p.productTitle.length > 20 ? p.productTitle.substring(0, 20) + '...' : p.productTitle,
			value: topProductsMetric === 'revenue' ? p.revenue.value / Math.pow(10, p.revenue.precision) : p.quantity,
			revenueAmount: p.revenue,
			quantity: p.quantity
		})) ?? []

	const sortedChannels = (salesByChannel?.channels ?? []).slice().sort((a, b) => b.revenue.value - a.revenue.value)
	const channelPageCount = Math.max(1, Math.ceil(sortedChannels.length / SALES_BY_CHANNEL_CHART_PAGE_SIZE))
	const channelPageSafe = Math.min(channelPage, channelPageCount - 1) // clamp if the list shrank (e.g. interval change)
	const salesByChannelChartData = sortedChannels
		.slice(
			channelPageSafe * SALES_BY_CHANNEL_CHART_PAGE_SIZE,
			channelPageSafe * SALES_BY_CHANNEL_CHART_PAGE_SIZE + SALES_BY_CHANNEL_CHART_PAGE_SIZE
		)
		.map(c => ({
			name: c.name,
			revenue: c.revenue.value / Math.pow(10, c.revenue.precision),
			revenueAmount: c.revenue
		}))

	const hourlyChartData =
		hourlyDistribution?.map(h => ({
			hour: `${h.hour}:00`,
			orders: h.orderCount
		})) ?? []

	const axisTickProps = { fontSize: 11 }
	const gridStroke = 'var(--border-base)'

	const changePeriodSearch = (patch: Partial<typeof search>) => {
		void navigate({ search: prev => ({ ...prev, ...patch }) })
	}

	// Basket anchor filters + min support are now inline controls that write straight to the URL.
	const changeProductUuids = (uuids: string[]) => {
		void navigate({ search: prev => ({ ...prev, productUuids: uuids.length ? uuids : undefined }) })
	}
	const changeCategoryUuids = (uuids: string[]) => {
		void navigate({ search: prev => ({ ...prev, categoryUuids: uuids.length ? uuids : undefined }) })
	}
	const changeExcludeProductUuids = (uuids: string[]) => {
		void navigate({ search: prev => ({ ...prev, excludeProductUuids: uuids.length ? uuids : undefined }) })
	}
	const changeExcludeCategoryUuids = (uuids: string[]) => {
		void navigate({ search: prev => ({ ...prev, excludeCategoryUuids: uuids.length ? uuids : undefined }) })
	}
	const changeMinSupport = (value: number) => {
		// Fold the default back to an absent param so the Basket URL stays clean.
		void navigate({
			search: prev => ({ ...prev, minSupport: value === DEFAULT_MIN_SUPPORT ? undefined : value })
		})
	}
	const changeProductSort = (sortBy: BasketPairSortField | undefined, sortOrder: 'asc' | 'desc' | undefined) => {
		void navigate({ search: prev => ({ ...prev, productSortBy: sortBy, productSortOrder: sortOrder }) })
	}
	const changeCategorySort = (sortBy: BasketPairSortField | undefined, sortOrder: 'asc' | 'desc' | undefined) => {
		void navigate({ search: prev => ({ ...prev, categorySortBy: sortBy, categorySortOrder: sortOrder }) })
	}

	const viewMeta = {
		sales: { title: L.Analytics.SalesAnalytics(), description: L.Analytics.SalesAnalyticsDescription() },
		users: { title: L.Analytics.UserAnalytics(), description: L.Analytics.UserAnalyticsDescription() },
		basket: { title: L.Analytics.BasketAnalysis.Title(), description: L.Analytics.BasketAnalysis.Description() }
	}[view]

	// Basket keeps its own channel param (`salesChannelUuids`) and adds the min-support selector.
	const basketFilters = (
		<div className='flex items-center gap-2'>
			<SalesChannelMultiSelect
				salesChannels={salesChannelList ?? []}
				value={salesChannelUuids ?? []}
				onChange={next =>
					navigate({
						search: { ...search, salesChannelUuids: next.length > 0 ? next : undefined }
					})
				}
				allLabel={L.Analytics.AllChannels()}
				isLoading={salesChannelsLoading}
				className='w-[13rem]'
			/>
			{/* Mirrors the Reports "Columns" control: the label sits on the trigger (grows to fill, so
			    the value + chevron stay right-aligned) and reads e.g. "Min. Co-occurrences  3". */}
			<Select
				value={String(minSupport ?? DEFAULT_MIN_SUPPORT)}
				onValueChange={next => changeMinSupport(Number(next))}
			>
				<Select.Trigger className='w-auto gap-2' aria-label={L.Analytics.BasketAnalysis.MinSupport()}>
					<span className='flex-1 truncate text-left'>{L.Analytics.BasketAnalysis.MinSupport()}</span>
					<span className='text-ui-fg-muted'>
						<Select.Value />
					</span>
				</Select.Trigger>
				<Select.Content>
					{MIN_SUPPORT_CHOICES.map(value => (
						<Select.Item key={value} value={String(value)}>
							{String(value)}
						</Select.Item>
					))}
				</Select.Content>
			</Select>
		</div>
	)

	const controlsVariant = ({ sales: 'sales', users: 'period', basket: 'navigation' } as const)[view]

	// Title/subtitle sit at the top of the page; view switching lives in the sidebar.
	const header = (
		<div className='flex flex-col items-start justify-between gap-4 pt-4 lg:flex-row lg:items-start'>
			<div className='space-y-1'>
				<Heading level='h2'>{viewMeta.title}</Heading>
				<Text className='text-ui-fg-subtle'>{viewMeta.description}</Text>
			</div>
			<AnalyticsPeriodControls
				// Remount when the view changes so the comparison-mode toggle re-seeds from the URL.
				key={view}
				search={search}
				onChange={changePeriodSearch}
				variant={controlsVariant}
				earliestSelectable={earliestSelectable}
				filterSlot={view === 'basket' ? basketFilters : undefined}
			/>
		</div>
	)

	// The report route turns comparison off by default (absent = off, 'previous' = previous period),
	// which is the inverse of the Sales screen (absent = previous). Map across when linking.
	const base = pickPeriodSearch(search)
	const reportSearch = {
		interval: base.interval,
		periodStart: base.periodStart,
		channels: base.channels,
		compareChannels: base.compareChannels,
		comparePeriod: toReportCompare(base.comparePeriod)
	}

	if (view === 'basket') {
		return (
			<Container className='rounded-none bg-transparent shadow-none'>
				<div className='space-y-6'>
					{header}
					<BasketAnalyticsView
						interval={interval}
						periodStart={periodStart}
						productUuids={productUuids}
						categoryUuids={categoryUuids}
						excludeProductUuids={excludeProductUuids}
						excludeCategoryUuids={excludeCategoryUuids}
						salesChannelUuids={salesChannelUuids}
						minSupport={minSupport ?? DEFAULT_MIN_SUPPORT}
						productSortBy={productSortBy}
						productSortOrder={productSortOrder}
						categorySortBy={categorySortBy}
						categorySortOrder={categorySortOrder}
						onProductUuidsChange={changeProductUuids}
						onCategoryUuidsChange={changeCategoryUuids}
						onExcludeProductUuidsChange={changeExcludeProductUuids}
						onExcludeCategoryUuidsChange={changeExcludeCategoryUuids}
						onProductSortChange={changeProductSort}
						onCategorySortChange={changeCategorySort}
					/>
				</div>
			</Container>
		)
	}

	if (view === 'users') {
		return (
			<Container className='rounded-none bg-transparent shadow-none'>
				<div className='space-y-6'>
					{header}
					<UserAnalyticsView interval={interval} periodStart={periodStart} comparePeriod={comparePeriod} />
				</div>
			</Container>
		)
	}

	return (
		<Container className='rounded-none bg-transparent shadow-none'>
			<div className='space-y-6'>
				{header}

				{isLoading ? (
					<div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
						{[...Array(3)].map((_, i) => (
							<Skeleton key={i} className='h-32' />
						))}
					</div>
				) : overview ? (
					<div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
						<KPICard
							title={L.Analytics.TotalRevenue()}
							current={
								overview.currentPeriod.revenue.value /
								Math.pow(10, overview.currentPeriod.revenue.precision)
							}
							previous={
								overview.previousPeriod.revenue.value /
								Math.pow(10, overview.previousPeriod.revenue.precision)
							}
							icon={DollarSign}
							formatter={val =>
								formatCurrency({
									value: val * Math.pow(10, overview.currentPeriod.revenue.precision),
									precision: overview.currentPeriod.revenue.precision
								})
							}
							comparisonLabel={comparisonLabel}
							hasComparison={hasComparison}
						/>
						<KPICard
							title={L.Analytics.Orders()}
							current={overview.currentPeriod.orderCount}
							previous={overview.previousPeriod.orderCount}
							icon={ShoppingCart}
							comparisonLabel={comparisonLabel}
							hasComparison={hasComparison}
						/>
						<KPICard
							title={L.Analytics.AverageOrderValue()}
							current={
								overview.currentPeriod.averageOrderValue.value /
								Math.pow(10, overview.currentPeriod.averageOrderValue.precision)
							}
							previous={
								overview.previousPeriod.averageOrderValue.value /
								Math.pow(10, overview.previousPeriod.averageOrderValue.precision)
							}
							icon={TrendingUp}
							formatter={val =>
								formatCurrency({
									value: val * Math.pow(10, overview.currentPeriod.averageOrderValue.precision),
									precision: overview.currentPeriod.averageOrderValue.precision
								})
							}
							comparisonLabel={comparisonLabel}
							hasComparison={hasComparison}
						/>
					</div>
				) : null}

				<Card>
					<CardHeader className='flex flex-row items-center justify-between'>
						<CardTitle className='text-sm font-medium'>
							<Link
								to='/analytics/reports'
								search={reportSearch}
								className='group inline-flex items-center gap-1 text-ui-fg-base transition-fg hover:underline'
							>
								{L.Analytics.RevenueOverTime()}
								<ArrowUpRight className='text-muted-foreground h-3.5 w-3.5 transition-fg group-hover:text-ui-fg-base' />
							</Link>
						</CardTitle>
					</CardHeader>
					<CardContent>
						<RevenueOverTimeChart
							isLoading={isLoading}
							rows={analytics?.revenueOverTime}
							comparisonRows={hasComparison ? analytics?.comparisonOverTime : undefined}
							comparisonLabel={comparisonLabel}
							currencyCode={displayCurrency}
						/>
					</CardContent>
				</Card>

				<div className='grid gap-4 md:grid-cols-2'>
					{/* Top products are not stored per channel in the rollup, so hide them while a channel
					    filter/comparison is active rather than show tenant-wide numbers that contradict the KPIs. */}
					{!channelFilterActive && (
						<Card>
							<CardHeader className='flex flex-row items-center justify-between gap-2'>
								<CardTitle className='text-sm font-medium'>
									<Link
										to='/analytics/reports'
										search={{ ...reportSearch, rows: 'product', sortBy: topProductsMetric }}
										className='group inline-flex items-center gap-1 text-ui-fg-base transition-fg hover:underline'
									>
										{L.Analytics.TopProducts()}
										<ArrowUpRight className='text-muted-foreground h-3.5 w-3.5 transition-fg group-hover:text-ui-fg-base' />
									</Link>
								</CardTitle>
								<div className='flex items-center gap-2'>
									<Select
										value={topProductsMetric}
										onValueChange={value => setTopProductsMetric(value as 'revenue' | 'quantity')}
									>
										<Select.Trigger className='h-8 w-[11rem]'>
											<span className='truncate'>
												{topProductsMetric === 'revenue'
													? L.Analytics.TopProductsByRevenue()
													: L.Analytics.TopProductsByQuantity()}
											</span>
										</Select.Trigger>
										<Select.Content>
											<Select.Item value='revenue'>
												{L.Analytics.TopProductsByRevenue()}
											</Select.Item>
											<Select.Item value='quantity'>
												{L.Analytics.TopProductsByQuantity()}
											</Select.Item>
										</Select.Content>
									</Select>
								</div>
							</CardHeader>
							<CardContent>
								{isLoading ? (
									<Skeleton className='h-[300px]' />
								) : topProductsSource && topProductsSource.length > 0 ? (
									<ChartContainer
										config={{
											value: { label: topProductsMetricLabel }
										}}
										className='h-[300px]'
									>
										<BarChart
											data={topProductsChartData}
											layout='vertical'
											margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
										>
											<CartesianGrid
												stroke={gridStroke}
												strokeDasharray='3 3'
												horizontal={false}
											/>
											<XAxis
												type='number'
												tickFormatter={
													topProductsMetric === 'revenue' ? formatCurrencyAxis : formatCount
												}
												tick={axisTickProps}
											/>
											<YAxis dataKey='name' type='category' width={150} tick={axisTickProps} />
											<ChartTooltip
												content={<ChartTooltipContent hideLabel />}
												formatter={(value: any, name: string, props: any) => {
													if (topProductsMetric === 'revenue') {
														return formatCurrency(props.payload.revenueAmount)
													}
													return formatCount(props.payload.quantity)
												}}
											/>
											<Bar
												dataKey='value'
												name={topProductsMetricLabel}
												radius={[0, 4, 4, 0]}
												strokeWidth={0}
												maxBarSize={28}
											>
												{topProductsChartData.map((_, index) => (
													<Cell
														key={`tp-${index}`}
														fill={
															CHART_PALETTE_TOP_PRODUCTS[
																index % CHART_PALETTE_TOP_PRODUCTS.length
															]
														}
													/>
												))}
											</Bar>
										</BarChart>
									</ChartContainer>
								) : (
									<div className='text-muted-foreground flex h-[300px] items-center justify-center'>
										<Text>{L.Analytics.NoData()}</Text>
									</div>
								)}
							</CardContent>
						</Card>
					)}

					<Card>
						<CardHeader className='flex flex-row items-center justify-between'>
							<CardTitle className='text-sm font-medium'>{L.Analytics.SalesByChannel()}</CardTitle>
							{channelPageCount > 1 && (
								<div className='flex items-center gap-1'>
									<IconButton
										size='small'
										variant='transparent'
										disabled={channelPageSafe === 0}
										onClick={() => setChannelPage(p => Math.max(0, p - 1))}
										tooltipText={L.Pagination.Previous()}
									>
										<ChevronLeft />
									</IconButton>
									<Text size='small' className='tabular-nums text-ui-fg-subtle'>
										{L.Analytics.ChannelsPageOf({
											current: channelPageSafe + 1,
											total: channelPageCount
										})}
									</Text>
									<IconButton
										size='small'
										variant='transparent'
										disabled={channelPageSafe >= channelPageCount - 1}
										onClick={() => setChannelPage(p => Math.min(channelPageCount - 1, p + 1))}
										tooltipText={L.Pagination.Next()}
									>
										<ChevronRight />
									</IconButton>
								</div>
							)}
						</CardHeader>
						<CardContent>
							{isLoading ? (
								<Skeleton className='h-[300px]' />
							) : salesByChannel && salesByChannel.channels.length > 0 ? (
								<ChartContainer
									config={{
										revenue: { label: L.Analytics.ChartLabels.Revenue() }
									}}
									className='h-[300px]'
								>
									<BarChart
										data={salesByChannelChartData}
										margin={{ top: 8, right: 8, left: 0, bottom: 24 }}
										barCategoryGap='18%'
									>
										<CartesianGrid stroke={gridStroke} strokeDasharray='3 3' />
										<XAxis
											dataKey='name'
											tick={{ fontSize: isMobile ? 10 : 11 }}
											interval={0}
											angle={isMobile ? -40 : -18}
											textAnchor='end'
											height={isMobile ? 64 : 48}
										/>
										<YAxis width={112} tickFormatter={formatCurrencyAxis} tick={axisTickProps} />
										<ChartTooltip
											content={<ChartTooltipContent hideLabel />}
											formatter={(value: any, name: string, props: any) => {
												if (props.payload.revenueAmount) {
													return formatCurrency(props.payload.revenueAmount)
												}
												return value
											}}
										/>
										<Bar
											dataKey='revenue'
											name={L.Analytics.ChartLabels.Revenue()}
											radius={[4, 4, 0, 0]}
											strokeWidth={0}
											maxBarSize={48}
										>
											{salesByChannelChartData.map((_, index) => (
												<Cell
													key={`ch-${index}`}
													fill={CHART_PALETTE_FILLS[index % CHART_PALETTE_FILLS.length]}
												/>
											))}
										</Bar>
									</BarChart>
								</ChartContainer>
							) : (
								<div className='text-muted-foreground flex h-[300px] items-center justify-center'>
									<Text>{L.Analytics.NoData()}</Text>
								</div>
							)}
						</CardContent>
					</Card>
				</div>

				{interval !== 'day' && !channelFilterActive && (
					<Card>
						<CardHeader>
							<CardTitle className='text-sm font-medium'>{L.Analytics.HourlyDistribution()}</CardTitle>
						</CardHeader>
						<CardContent>
							{isLoading ? (
								<Skeleton className='h-[300px]' />
							) : hourlyDistribution && hourlyDistribution.length > 0 ? (
								<ChartContainer
									config={{
										orders: {
											label: L.Analytics.ChartLabels.Orders(),
											color: CHART_PALETTE_FILLS[0]
										}
									}}
									className='h-[300px]'
								>
									<BarChart
										data={hourlyChartData}
										margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
										barCategoryGap='12%'
									>
										<CartesianGrid stroke={gridStroke} strokeDasharray='3 3' />
										<XAxis dataKey='hour' tick={axisTickProps} interval={2} />
										<YAxis width={48} allowDecimals={false} tick={axisTickProps} />
										<ChartTooltip content={<ChartTooltipContent />} />
										<Bar
											dataKey='orders'
											name={L.Analytics.ChartLabels.Orders()}
											fill={CHART_PALETTE_FILLS[0]}
											radius={[4, 4, 0, 0]}
											strokeWidth={0}
											maxBarSize={32}
										/>
									</BarChart>
								</ChartContainer>
							) : (
								<div className='text-muted-foreground flex h-[300px] items-center justify-center'>
									<Text>{L.Analytics.NoData()}</Text>
								</div>
							)}
						</CardContent>
					</Card>
				)}
			</div>
		</Container>
	)
}
